import React, { useCallback } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    useTheme,
} from "@mui/material";
import {
    usePluginUpdateStore,
    useShowBelowMinAlert,
    useShowBelowMinModal,
} from "../../stores/usePluginUpdateStore";

/**
 * Persistent below-minimum reminder for when the installed plugin is older than
 * the minimum supported version. Two pieces, mounted once at the app shell:
 *
 *  - a non-dismissible warning bar pinned to the top of the shell, visible for
 *    as long as the plugin is below minimum (README §6.12 / §8.8: the reminder
 *    is persistent);
 *  - a closable blocking dialog (Close button + ESC) shown until dismissed this
 *    session. The dismissal is session-only (never persisted), so the modal
 *    re-opens next launch while the plugin is still below minimum.
 */
const PluginUpdateRequiredModal: React.FC = () => {
    const theme = useTheme();
    const installedVersion = usePluginUpdateStore(
        (state) => state.installedVersion,
    );
    const minSupportedVersion = usePluginUpdateStore(
        (state) => state.minSupportedVersion,
    );
    const state = usePluginUpdateStore((s) => s.state);
    const dismissBelowMinModal = usePluginUpdateStore(
        (s) => s.dismissBelowMinModal,
    );
    const showAlert = useShowBelowMinAlert();
    const showModal = useShowBelowMinModal();

    const handleClose = useCallback(() => {
        dismissBelowMinModal();
    }, [dismissBelowMinModal]);

    if (!showAlert) {
        return null;
    }

    const alertText =
        state === "READY_TO_APPLY"
            ? "Plugin update required — restart Revit to apply the update."
            : "Plugin update required — download the update and restart Revit.";

    return (
        <>
            <Box
                data-testid="plugin-update-required-alert"
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.snackbar + 2,
                }}
            >
                <Alert
                    severity="warning"
                    variant="filled"
                    square
                    sx={{ justifyContent: "center" }}
                >
                    {alertText}
                </Alert>
            </Box>

            <Dialog
                open={showModal}
                onClose={handleClose}
                aria-labelledby="plugin-update-required-title"
                aria-describedby="plugin-update-required-description"
                data-testid="plugin-update-required-modal"
            >
                <DialogTitle id="plugin-update-required-title">
                    Plugin update required
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="plugin-update-required-description">
                        Your PipeEX plugin {installedVersion} is below the
                        minimum supported version {minSupportedVersion}. Some
                        features may stop working until you update and restart
                        Revit.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        data-testid="plugin-update-required-close"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PluginUpdateRequiredModal;
